import { TimeManagementListItem } from '@myapp/contract'
import { getTopPx, getHeightPx } from '../model/calendarConstants'
import { TIME_ENTRY_CATEGORY_COLOR, getTimeEntryCategoryLabel } from '../../../entities/time-entry'

interface Props {
  entry: TimeManagementListItem
  dayIso: string
  /** ISO datetime of the technician's shift start — used to clamp all-day entries */
  shiftStart?: string | null
  /** ISO datetime of the technician's shift end — used to clamp all-day entries */
  shiftEnd?: string | null
  onClick?: () => void
  left?: string
  zIndex?: number
  interactive?: boolean
  isDragging?: boolean
  overrideTopPx?: number
  onPointerDown?: (e: React.PointerEvent<HTMLDivElement>) => void
}

/** Returns true when the entry spans the whole calendar day (00:00 – 23:xx). */
export function isAllDayTMEntry(entry: TimeManagementListItem, dayIso: string): boolean {
  const start = new Date(entry.startTime)
  const end = new Date(entry.endTime)
  return (
    start.toISOString().slice(0, 10) <= dayIso &&
    start.getHours() === 0 &&
    start.getMinutes() === 0 &&
    end.toISOString().slice(0, 10) >= dayIso &&
    end.getHours() >= 23
  )
}

export const WorkerTMBlock = ({
  entry, dayIso, shiftStart, shiftEnd,
  onClick, left, zIndex,
  interactive, isDragging, overrideTopPx, onPointerDown,
}: Props) => {
  const dayStart = new Date(`${dayIso}T00:00:00`)
  const dayEnd = new Date(`${dayIso}T23:59:59.999`)

  const allDay = isAllDayTMEntry(entry, dayIso)

  const clampedStart = allDay && shiftStart
    ? new Date(`${dayIso}T${new Date(shiftStart).toISOString().slice(11, 19)}`)
    : new Date(Math.max(new Date(entry.startTime).getTime(), dayStart.getTime()))

  const clampedEnd = allDay && shiftEnd
    ? new Date(`${dayIso}T${new Date(shiftEnd).toISOString().slice(11, 19)}`)
    : new Date(Math.min(new Date(entry.endTime).getTime(), dayEnd.getTime()))

  const durationMinutes = Math.round((clampedEnd.getTime() - clampedStart.getTime()) / 60_000)

  if (durationMinutes <= 0) return null

  const top = overrideTopPx ?? getTopPx(clampedStart.toISOString())
  const height = Math.max(getHeightPx(durationMinutes), 14)

  return (
    <div
      data-block
      className={`absolute right-0.5 flex flex-col justify-start px-1 text-white text-[12px] font-semibold rounded overflow-hidden ${
        interactive
          ? `cursor-grab active:cursor-grabbing ${isDragging ? 'opacity-100' : 'opacity-80 hover:opacity-100'}`
          : onClick ? 'cursor-pointer' : ''
      }`}
      style={{
        top,
        height,
        left: left ?? '2px',
        zIndex: zIndex ?? 10,
        backgroundColor: TIME_ENTRY_CATEGORY_COLOR,
        touchAction: interactive ? 'none' : undefined,
        transition: isDragging ? undefined : 'top 150ms ease',
      }}
      onPointerDown={onPointerDown}
      onClick={onPointerDown ? undefined : onClick}
    >
      <span className="leading-tight">{getTimeEntryCategoryLabel(entry.category)}</span>
    </div>
  )
}
